import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { useEnquiry } from "@/context/EnquiryContext";
import { useLang } from "@/context/LanguageContext";
import { productEnquiryUrl } from "@/lib/site";

export const EnquiryModal = () => {
  const { open, setOpen, product } = useEnquiry();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", mobile: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({ name: "", mobile: "" });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("err_name");
    if (!/^\d{10}$/.test(form.mobile.trim())) e.mobile = t("err_mobile");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const url = productEnquiryUrl({ name: form.name.trim(), mobile: form.mobile.trim(), product });
    window.open(url, "_blank");
    toast.success(t("toast_redirect"), { description: product });
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="enquiry-modal"
        >
          <div className="absolute inset-0 bg-navy-950/85 backdrop-blur-md" onClick={() => setOpen(false)} data-testid="enquiry-modal-overlay" />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gold/25 bg-navy-800"
          >
            <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full radial-glow" />
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 z-10 text-white/60 hover:text-gold transition-colors" aria-label="Close" data-testid="enquiry-modal-close">
              <X size={22} />
            </button>

            <div className="relative p-8">
              <p className="label-eyebrow mb-2">{t("modal_eyebrow")}</p>
              <h3 className="font-serif text-3xl text-white leading-tight">{product}</h3>
              <p className="mt-2 text-sm text-white/55 font-light">{t("modal_subtitle")}</p>

              <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">{t("modal_name")}</label>
                  <div className="relative">
                    <User size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/60" />
                    <input
                      data-testid="enquiry-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("modal_ph_name")}
                      className="w-full rounded-lg border border-gold/20 bg-navy-900 pl-10 pr-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-name-error">{errors.name}</p>}
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">{t("modal_mobile")}</label>
                  <div className="relative">
                    <Phone size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/60" />
                    <input
                      data-testid="enquiry-mobile-input"
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      inputMode="numeric"
                      placeholder={t("modal_ph_mobile")}
                      className="w-full rounded-lg border border-gold/20 bg-navy-900 pl-10 pr-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  {errors.mobile && <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-mobile-error">{errors.mobile}</p>}
                </div>

                <button
                  type="submit"
                  data-testid="enquiry-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors"
                >
                  <MessageCircle size={17} />
                  {t("send_whatsapp")}
                </button>
                <p className="text-center text-[11px] text-white/35">{t("modal_footnote")}</p>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
